/* eslint-disable react-hooks/set-state-in-effect */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { db, type PaletteRecord } from '../lib/database';

// ── Context shape ──────────────────────────────────────────────────────
export interface PaletteContextValue {
  currentPalette: PaletteRecord | null;
  updateCurrentPalette: (updates: Partial<PaletteRecord>) => Promise<void>;
  palettes: PaletteRecord[];
  setCurrentPalette: (palette: PaletteRecord | null) => void;
  refreshPalettes: () => Promise<void>;
}

// ── Context ────────────────────────────────────────────────────────────
const PaletteContext = createContext<PaletteContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────
export function PaletteProvider({ children }: { children: ReactNode }) {
  const [palettes, setPalettes] = useState<PaletteRecord[]>([]);
  const [currentPalette, setCurrentPalette] = useState<PaletteRecord | null>(
    null,
  );
  const currentPaletteIdRef = useRef<string | null>(null);

  useEffect(() => {
    currentPaletteIdRef.current = currentPalette?.id ?? null;
  }, [currentPalette?.id]);

  const refreshPalettes = useCallback(async () => {
    const all = await db.getAllPalettes();
    setPalettes(all);
  }, []);

  const updateCurrentPalette = useCallback(
    async (updates: Partial<PaletteRecord>) => {
      const id = currentPaletteIdRef.current;
      if (!id) return;

      await db.updatePalette(id, updates);
      const updated = await db.getPalette(id);
      setCurrentPalette(updated || null);
      await refreshPalettes();
    },
    [refreshPalettes],
  );

  useEffect(() => {
    refreshPalettes();
  }, [refreshPalettes]);

  return (
    <PaletteContext.Provider
      value={{
        currentPalette,
        updateCurrentPalette,
        palettes,
        setCurrentPalette,
        refreshPalettes,
      }}
    >
      {children}
    </PaletteContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────
export function usePaletteContext() {
  const ctx = useContext(PaletteContext);
  if (!ctx) {
    throw new Error(
      'usePaletteContext must be used within a <PaletteProvider>',
    );
  }
  return ctx;
}
